import { useCallback, useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { FirstAid, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { StatusPill, dayRange, ukDate, isoDay } from "./hrUtils";

/** Sickness: report (self or, for Ops, on someone's behalf), close when back, history. */
export const SicknessTab = ({ userId, isSelf, onChanged }) => {
  const [data, setData] = useState({ sickness: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ start_date: isoDay(new Date()), end_date: "", reason: "" });
  const [back, setBack] = useState(null);
  const [backForm, setBackForm] = useState({ end_date: isoDay(new Date()), return_note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data: d } = await API.get(`/hr/sickness${userId ? `?user_id=${userId}` : ""}`); setData(d); } catch (e) { toast.error(formatApiError(e)); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const report = async () => {
    setBusy(true);
    try {
      await API.post("/hr/sickness", { ...form, end_date: form.end_date || null, user_id: userId || undefined });
      toast.success("Sickness reported — the Operations Directors have been told");
      setOpen(false); setForm({ start_date: isoDay(new Date()), end_date: "", reason: "" }); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const close = async () => {
    setBusy(true);
    try {
      await API.put(`/hr/sickness/${back.id}`, backForm);
      toast.success("Welcome back — record closed"); setBack(null); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const openRec = data.sickness.find((s) => s.status === "open");
  return (
    <div className="space-y-5" data-testid="hr-sickness-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-4 text-sm">
          <span><strong data-testid="hr-sick-days">{data.days_this_year ?? 0}</strong> <span className="text-muted-foreground">working days this year</span></span>
          <span><strong data-testid="hr-sick-episodes">{data.episodes_this_year ?? 0}</strong> <span className="text-muted-foreground">episodes</span></span>
        </div>
        {!openRec && <Button variant="outline" onClick={() => setOpen(true)} data-testid="hr-report-sick-btn"><FirstAid className="h-4 w-4 mr-2" /> {isSelf ? "Report sickness" : "Record sickness"}</Button>}
      </div>
      {openRec && (
        <div className="rounded-xl border border-red-300 bg-red-50/70 dark:bg-red-950/20 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="hr-sick-open">
          <div>
            <p className="font-semibold text-red-900 dark:text-red-200">Off sick since {ukDate(openRec.start_date)}</p>
            <p className="text-sm text-red-800/80 dark:text-red-200/80">{openRec.reason || "No reason given"} · {openRec.days}d so far</p>
          </div>
          <Button onClick={() => { setBack(openRec); setBackForm({ end_date: isoDay(new Date()), return_note: "" }); }} data-testid="hr-sick-back-btn"><CheckCircle className="h-4 w-4 mr-2" /> {isSelf ? "I'm back at work" : "Mark as back"}</Button>
        </div>
      )}
      <div className="rounded-xl border border-border divide-y divide-border">
        <p className="px-3 py-2 text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">History</p>
        {data.sickness.length === 0 && <p className="p-4 text-sm text-muted-foreground">No sickness recorded.</p>}
        {data.sickness.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-3 px-3 py-2.5" data-testid={`hr-sick-row-${s.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium">{s.end_date ? dayRange(s.start_date, s.end_date) : `${ukDate(s.start_date)} – ongoing`} <span className="text-muted-foreground font-normal">· {s.days}d</span></p>
              <p className="text-xs text-muted-foreground truncate">{s.reason}{s.return_note ? ` — back: ${s.return_note}` : ""}</p>
            </div>
            <StatusPill status={s.status} />
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{isSelf ? "Report sickness" : "Record sickness"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>First day off</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="hr-sick-start" /></div>
            <div><Label>Back at work on (leave blank if still off)</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="hr-sick-end" /></div>
            <div><Label>Reason (optional)</Label><Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Flu" data-testid="hr-sick-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={report} disabled={busy} data-testid="hr-sick-submit">Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!back} onOpenChange={(o) => !o && setBack(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Back at work</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Last day off</Label><Input type="date" value={backForm.end_date} onChange={(e) => setBackForm({ ...backForm, end_date: e.target.value })} data-testid="hr-sick-back-end" /></div>
            <div><Label>Return note (optional)</Label><Textarea rows={2} value={backForm.return_note} onChange={(e) => setBackForm({ ...backForm, return_note: e.target.value })} placeholder="Fit note received, phased return…" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBack(null)}>Cancel</Button>
            <Button onClick={close} disabled={busy} data-testid="hr-sick-back-submit">Close record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
